import * as React from 'react';
import { useState, useEffect, createContext } from 'react';
import { flushSync } from 'react-dom';
import { Snackbar, Alert, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';


interface DefinitionDataProviderProps {
  children: any;
  overrideFullData:any;
  overrideSetFullData:any;
  overridePolicyMessages:any;
  overrideSetPolicyMessages:any;
  suppressOCRFetch?:boolean;
}


type MouseSelectionMode = "pan" | "select" | null;
export const MouseSelectionModePan: MouseSelectionMode = "pan";
export const MouseSelectionModeSelect: MouseSelectionMode = "select";

export interface RegionOfInterestContextProps {

    mouseSelectionMode: MouseSelectionMode;
    setMouseSelectionMode: (mode: MouseSelectionMode) => void;
    mouseModeUserSelectable: boolean;
    setMouseModeUserSelectable: (selectable: boolean) => void;
    regionsOfInterest: Array<{  x: number; y: number; width: number; height: number; selectionStart: string, style: string, label: string, labelStyle: string, labelTransform: string, userSelection: boolean  }>;
    setRegionsOfInterest: (regions: Array<{ x: number; y: number; width: number; height: number; selectionStart: string, style: string, label: string, labelStyle: string, labelTransform: string, userSelection: boolean }>) => void;
    selectedRegion: { x: number; y: number; width: number; height: number; type: string, userSelection: boolean };
    setSelectedRegion: (region: { x: number; y: number; width: number; height: number; selectionStart: string, style: string, label: string, labelStyle: string, labelTransform: string, userSelection: boolean }) => void;
    minRegionWidth: number;
    minRegionHeight: number;
    defaultRegionStyle: { stroke: string; fill: string; strokeWidth: number };
    setDefaultRegionStyle: { stroke: string; fill: string; strokeWidth: number };
}

export interface CurrentAttachmentContextProps {
  currentAttachment: any;
  setCurrentAttachment: (attachment: any) => void;
  currentPage: any;
  setCurrentPage: (page: any) => void;
  rotation: any;
  setRotation: (r: any) => void;
  scaleMode?: 'fit-width' | 'fit-height' | 'best-fit' | null;
}


/**
 * Form-global transient banner channel. Holds a single message string (not a list) which is
 * displayed as a top-center Snackbar and self-clears after 7 seconds.
 *
 * Distinct from PolicyContext: policy messages are path-anchored and render inline in the
 * matching section, persisting until cleared per-path. General messages carry no path and are
 * not part of that lifecycle. The backend ships them inside the normal policyMessages array
 * tagged with severity 5 (policy.MessageSeverityGeneralInformation); consumers demultiplex on
 * arrival, filtering severity 5 out of policy messages and routing it here instead.
 */
export const GeneralMessageContext = createContext();
export const DataContext = createContext();
export const PolicyContext = createContext();
export const DirtyContext = createContext();
export const RegionOfInterestContext = createContext();
export const CurrentAttachmentContext = createContext();
export const ActionContext = createContext();

/** Callback to reset all provider state to initial values. Optional callback runs after reset is applied. */
export const ResetContext = createContext<((onComplete?: () => void) => void) | null>(null);

export interface ITriggeredEvent {
    path: string;
    type: string; // e.g., 'KeyEvent', 'click', etc.
    event: React.KeyboardEvent<HTMLInputElement>;
    keyDef: { key: string; shiftKey?: boolean; ctrlKey?: boolean; altKey?: boolean; };
}

// This context will hold the last triggered event object.
// Components can listen for changes to this value.
export const EventContext = createContext<[
    ITriggeredEvent | null,
    (event: ITriggeredEvent | null) => void
] | []>([]);

export const DefinitionDataProvider: React.FC<DefinitionDataProviderProps> = ({
  children,
  overrideFullData,
  overrideSetFullData,
  overridePolicyMessages,
  overrideSetPolicyMessages,
  suppressOCRFetch
}) => {

  const [fullData, setFullData] = overrideSetFullData != null ? [overrideFullData, overrideSetFullData] : useState(null); 
  const [policyMessages, setPolicyMessages] = overrideSetPolicyMessages != null ? [overridePolicyMessages, overrideSetPolicyMessages] : useState(null); 
  const [formDirty, setFormDirty] = useState(false); 
  const [regionsOfInterest, setRegionsOfInterest] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [defaultRegionStyle, setDefaultRegionStyle] = useState({ stroke: 'rgba(86, 203, 243, 0.6)', fill: 'rgba(86, 203, 243, 0.3)', strokeWidth: 1 });
  const [minRegionWidth, setMinRegionWidth] = useState(0);
  const [minRegionHeight, setMinRegionHeight] = useState(0);
  const [mouseSelectionMode, setMouseSelectionMode] = useState('pan');
  const [mouseModeUserSelectable, setMouseModeUserSelectable] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState(undefined)
  const [suppressOCRData, setSuppressOCRData] = useState(suppressOCRFetch);
  const [roiPath, setRoiPath] = useState(null);
  const [icgOCRData, setICGOCRData] = useState(null)
  const [rawOCRData, setRawOCRData] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [initAction, setInitAction] = useState(null)
  const [scaleMode, setScaleMode] = useState<'fit-width' | 'fit-height' | 'best-fit' | null>(null);
  const [generalMessage, setGeneralMessage] = useState<string | null>(null)


  // Triggered event context
  const [triggeredEvent, setTriggeredEvent] = useState<ITriggeredEvent | null>(null);


  const rawSetPolicyMessages = (messages) => {
    setPolicyMessages(messages);
  }

  // General messages are advisory only and carry no path, so nothing else clears them.
  // The cleanup matters: without it a timer from a previous message would fire against a newer
  // one and dismiss it early. Explicit undefined return satisfies noImplicitReturns.
  useEffect(() => {
    if (generalMessage == null) {
      return undefined
    }
    const timer = setTimeout(() => {
      setGeneralMessage(null)
    }, 7000)
    return () => clearTimeout(timer)
  }, [generalMessage])

  const resetAll = React.useCallback((onComplete?: () => void) => {
    flushSync(() => {
      setFullData(null);
      setPolicyMessages(null);
      setFormDirty(false);
      setRegionsOfInterest([]);
      setSelectedRegion(null);
      setDefaultRegionStyle({ stroke: 'rgba(86, 203, 243, 0.6)', fill: 'rgba(86, 203, 243, 0.3)', strokeWidth: 1 });
      setMinRegionWidth(0);
      setMinRegionHeight(0);
      setMouseSelectionMode('pan');
      setMouseModeUserSelectable(false);
      setCurrentAttachment(undefined);
      setRoiPath(null);
      setICGOCRData(null);
      setRawOCRData(null);
      setCurrentPage(1);
      setRotation(0);
      setInitAction(null);
      setScaleMode(null);
      setTriggeredEvent(null);
      setGeneralMessage(null);
    });
    onComplete?.();
  }, [
    setFullData, setPolicyMessages, setFormDirty, setRegionsOfInterest, setSelectedRegion,
    setDefaultRegionStyle, setMinRegionWidth, setMinRegionHeight, setMouseSelectionMode, setMouseModeUserSelectable,
    setCurrentAttachment, setRoiPath, setICGOCRData, setRawOCRData, setCurrentPage, setRotation,
    setInitAction, setScaleMode, setTriggeredEvent, setGeneralMessage
  ]);

  // console.log("icg-definition-data-provier: suppressOCRFetch", suppressOCRFetch, suppressOCRData);

  return (
    <ResetContext.Provider value={resetAll}>
    <DataContext.Provider value={[fullData, setFullData]}>
      <EventContext.Provider value={[triggeredEvent, setTriggeredEvent]}>
        <PolicyContext.Provider value={[policyMessages, rawSetPolicyMessages]}>
          <ActionContext.Provider value={[initAction, setInitAction]}>
            <CurrentAttachmentContext.Provider value={[currentAttachment, setCurrentAttachment, currentPage, setCurrentPage, rotation, setRotation, scaleMode, setScaleMode, roiPath, setRoiPath, icgOCRData, setICGOCRData, rawOCRData, setRawOCRData, suppressOCRData, setSuppressOCRData]}>
              <RegionOfInterestContext.Provider value={{ selectedRegion, setSelectedRegion, regionsOfInterest, setRegionsOfInterest, mouseSelectionMode, setMouseSelectionMode, mouseModeUserSelectable, setMouseModeUserSelectable, minRegionWidth, setMinRegionWidth, minRegionHeight, setMinRegionHeight, defaultRegionStyle, setDefaultRegionStyle }}>
                <DirtyContext.Provider value={[formDirty, setFormDirty]}>
                  <GeneralMessageContext.Provider value={[generalMessage, setGeneralMessage]}>
                    {children}
                    {/* Rendered here rather than in DefinitionSection: that component is recursive,
                        so rendering per-section would produce one duplicate Snackbar (and one
                        dismissal timer) per mounted section for the same message. */}
                    {generalMessage && <Snackbar
                      anchorOrigin={{ vertical: "top", horizontal: "center" }}
                      open={generalMessage != null}
                    ><Alert>{generalMessage}&nbsp;<IconButton
                      aria-label="close"
                      color="inherit"
                      sx={{ display: "none", backgroundColor: "#b9cbba", width: 10, height: 10, padding: 2, top: -1 }}
                      onClick={() => { setGeneralMessage(null) }}
                    >
                      <CloseIcon />
                    </IconButton></Alert></Snackbar>}
                  </GeneralMessageContext.Provider>
                </DirtyContext.Provider>
              </RegionOfInterestContext.Provider>
            </CurrentAttachmentContext.Provider>
          </ActionContext.Provider>
        </PolicyContext.Provider>
      </EventContext.Provider>
    </DataContext.Provider>
    </ResetContext.Provider>
  );

}


export default DefinitionDataProvider;